import { useState, useEffect, type ReactNode } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ALL_TRAITS, traitLabel } from "@/lib/game/dialogue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getMarketplaceListing,
  buyListing,
  cancelListing,
  placeBid,
} from "@/lib/game/marketplace.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/game/Panel";
import { PortraitFrame } from "@/components/game/PortraitFrame";
import { Pill, RarityTag, TypeBadge } from "@/components/game/Badges";
import { GoldAmount, ItemIcon, MicroLabel, ScreenContainer } from "@/components/game/ScreenKit";
import { URGENT_MS, creatureTypeOf, formatRemaining, rarityOf } from "@/lib/game/marketplace-view";
import { cn } from "@/lib/utils";
import { ArrowLeft, Gavel, Hourglass, Loader2, Egg as EggIcon, Share2, Store } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { RouteError, RouteNotFound } from "@/components/game/RouteError";
import { tServerError } from "@/lib/i18n/server-error";

export const Route = createFileRoute("/_app/game/marketplace/$listingId")({
  component: ListingDetail,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
});

/**
 * Wert-Kachel wie auf der Sammelkarte: Ink-Fläche, große Zahl, Mikro-Label.
 *
 * ⚠️ Das ist eine zweite Fassung der Stat-Kachel aus `CreatureCard` — die
 * Sammelkarte selbst passt hier nicht (sie verlangt einen Typ und ein Level,
 * beides fehlt einem Ei, und ihr Hochformat kollidiert mit dem Datenblatt
 * daneben). Solange beide nebeneinander stehen, MUSS jede Änderung an der
 * einen hier nachgezogen werden. Deshalb liegt der Umzug in eine geteilte
 * Kachel-Komponente als Meldung beim Team.
 *
 * `legendary` ist genau so ein nachgezogener Zweig: Auf legendärem Grund
 * (`Panel tone="boss"`, wärmerer Ton) schluckt der Untergrund das Orange, die
 * Zahl geht deshalb auf Gold — dieselbe Regel wie in `CreatureCard`.
 */
function StatTile({
  label,
  value,
  legendary,
}: {
  label: string;
  value: number;
  legendary?: boolean;
}) {
  return (
    <div className="rounded-md bg-ink px-1 py-1.5 text-center">
      <b
        className={cn(
          "block font-display text-lg font-normal tabular-nums",
          legendary ? "text-gold" : "text-primary",
        )}
      >
        {value}
      </b>
      <span className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
    </div>
  );
}

/** Zeile im Datenblatt (Spezies, Stufe, Wesen). */
function FactRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t-2 border-ink/40 py-1.5 first:border-t-0">
      <MicroLabel className="shrink-0">{label}</MicroLabel>
      <span className="min-w-0 text-right text-[13px] font-extrabold [overflow-wrap:anywhere]">
        {children}
      </span>
    </div>
  );
}

function ListingDetail() {
  const { t, i18n } = useTranslation("game");
  const { t: tc } = useTranslation("common");
  const { listingId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const fetcher = useServerFn(getMarketplaceListing);
  const buyer = useServerFn(buyListing);
  const canceller = useServerFn(cancelListing);
  const bidder = useServerFn(placeBid);

  const [bidAmount, setBidAmount] = useState<string>("");
  const [now, setNow] = useState(Date.now());
  const [me, setMe] = useState<string | null>(null);

  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setMe(data.user?.id ?? null));
  }, []);

  const q = useQuery({
    queryKey: ["marketplace", "listing", listingId],
    queryFn: () => fetcher({ data: { id: listingId } }),
    refetchInterval: 10_000,
  });

  const listing = q.data?.listing;
  const bids = q.data?.bids ?? [];

  const isOwn = listing && me && listing.seller_id === me;
  const isAuction = listing?.listing_type === "auction";
  const minBid = isAuction
    ? listing.current_bid != null
      ? listing.current_bid + Math.max(1, Math.floor(listing.current_bid * 0.05))
      : (listing.starting_price ?? 1)
    : 0;

  // Default bid input on min
  useEffect(() => {
    if (isAuction && bidAmount === "") setBidAmount(String(minBid));
  }, [isAuction, minBid, bidAmount]);

  const buyM = useMutation({
    mutationFn: () => buyer({ data: { id: listingId } }),
    onSuccess: () => {
      toast.success(t("marketplace.detail.bought"));
      qc.invalidateQueries({ queryKey: ["marketplace"] });
      qc.invalidateQueries({ queryKey: ["my-monster"] });
      qc.invalidateQueries({ queryKey: ["my-monsters"] });
      qc.invalidateQueries({ queryKey: ["items"] });
      navigate({ to: "/game/marketplace" });
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const bidM = useMutation({
    mutationFn: (amt: number) => bidder({ data: { id: listingId, amount: amt } }),
    onSuccess: () => {
      toast.success(t("marketplace.detail.bidPlaced"));
      qc.invalidateQueries({ queryKey: ["marketplace"] });
      qc.invalidateQueries({ queryKey: ["my-monster"] });
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  const cancelM = useMutation({
    mutationFn: () => canceller({ data: { id: listingId } }),
    onSuccess: () => {
      toast.success(t("marketplace.detail.cancelled"));
      qc.invalidateQueries({ queryKey: ["marketplace"] });
      navigate({ to: "/game/marketplace" });
    },
    onError: (e: Error) => toast.error(tServerError(e)),
  });

  if (q.isLoading) {
    return (
      <ScreenContainer>
        <Panel contentClassName="flex items-center justify-center px-3 py-12 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" strokeWidth={2.5} />
        </Panel>
      </ScreenContainer>
    );
  }
  if (!listing) {
    // Ein Ladefehler ist kein „nicht gefunden" — sonst behauptet die Seite,
    // das Angebot sei weg, obwohl sie es nur nicht laden konnte.
    return (
      <ScreenContainer>
        <Panel contentClassName="px-3 py-12 text-center text-sm text-muted-foreground">
          {q.isError ? tc("errors.generic") : t("marketplace.empty")}
        </Panel>
        <div className="mt-3.5">
          <Button variant="outline" size="sm" asChild>
            <Link to="/game/marketplace">
              <ArrowLeft strokeWidth={2.5} />
              {t("marketplace.detail.back")}
            </Link>
          </Button>
        </div>
      </ScreenContainer>
    );
  }

  const img = listing.snapshot.image_url ?? null;
  const type = creatureTypeOf(listing.snapshot.type, listing.snapshot.stats?.type);
  const rarity = rarityOf(listing.item_kind, listing.snapshot.rarity);
  const legendary = rarity === "legendary";
  const name = listing.snapshot.name ?? "—";
  const isEgg = listing.item_kind !== "monster";
  const trait = ALL_TRAITS.find((tr) => tr === listing.snapshot.personality_trait) ?? null;

  const ended = listing.ends_at && new Date(listing.ends_at).getTime() <= now;
  const remainingMs = listing.ends_at ? new Date(listing.ends_at).getTime() - now : null;
  const urgent = remainingMs != null && remainingMs > 0 && remainingMs < URGENT_MS;
  const hasBid = isAuction && listing.current_bid != null;
  const currentPrice = hasBid ? listing.current_bid : listing.starting_price;

  const stats: Array<[string, number]> = [];
  if (listing.snapshot.hp_max != null) stats.push(["HP", listing.snapshot.hp_max]);
  if (listing.snapshot.atk != null) stats.push(["ATK", listing.snapshot.atk]);
  if (listing.snapshot.def != null) stats.push(["DEF", listing.snapshot.def]);
  // Kein SPD mehr: spd wird im Zonenkampf in keiner Formel gelesen (Stefan, 09.09.2026).
  // Der Wert bleibt im gespeicherten Snapshot liegen, er wird nur nicht gezeigt.

  async function handleShare() {
    const url = `${window.location.origin}/game/marketplace/${listingId}`;
    const title = listing?.snapshot.name ?? t("marketplace.title");
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title, url });
        return;
      }
    } catch {
      // user cancelled or share failed → fall back to clipboard
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("marketplace.detail.linkCopied"));
    } catch {
      toast.error(url);
    }
  }

  return (
    <ScreenContainer>
      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/game/marketplace"
          className="inline-flex min-h-11 items-center gap-1.5 text-sm font-extrabold text-muted-foreground no-underline hover:text-foreground sm:min-h-9"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> {t("marketplace.detail.back")}
        </Link>
        <Button variant="outline" size="sm" onClick={handleShare}>
          <Share2 strokeWidth={2.5} />
          {t("marketplace.detail.share")}
        </Button>
      </div>

      <div className="grid gap-3.5 md:grid-cols-[minmax(0,1fr)_320px]">
        <Panel tone={legendary ? "boss" : "default"} holo={legendary} grid contentClassName="p-3.5">
          <div className="grid gap-3.5 sm:grid-cols-[220px_minmax(0,1fr)]">
            <div className="mx-auto w-full max-w-[260px] sm:mx-0">
              {isEgg && !img ? (
                <div className="grid place-items-center">
                  <ItemIcon
                    icon={<EggIcon className="h-20 w-20" strokeWidth={2.5} />}
                    rarity={rarity ?? "common"}
                    size={200}
                  />
                </div>
              ) : (
                <div className="relative aspect-square w-full">
                  <PortraitFrame
                    src={img}
                    alt={name}
                    size={220}
                    ringColor={type ? `var(--type-${type})` : undefined}
                    className="!h-full !w-full rounded-lg"
                  />
                </div>
              )}
            </div>

            <div className="min-w-0">
              <h1 className="text-[26px] leading-tight [overflow-wrap:anywhere]">{name}</h1>
              <div className="mt-1 text-[12px] text-muted-foreground">
                {t("marketplace.detail.seller")}: {listing.seller_name ?? "?"}
              </div>

              <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                <Pill style={{ background: "var(--secondary)", color: "var(--foreground)" }}>
                  {isAuction ? (
                    <Gavel className="h-3 w-3" strokeWidth={2.5} />
                  ) : (
                    <Store className="h-3 w-3" strokeWidth={2.5} />
                  )}
                  {t(`marketplace.listingType.${listing.listing_type}`)}
                </Pill>
                {type ? <TypeBadge type={type} /> : null}
                {rarity ? <RarityTag rarity={rarity} /> : null}
                {!isEgg && listing.snapshot.level != null ? (
                  <span className="text-[12px] font-extrabold tabular-nums text-muted-foreground">
                    {t("marketplace.card.level")} {listing.snapshot.level}
                  </span>
                ) : null}
              </div>

              {stats.length > 0 && (
                <div
                  className="mt-3.5 grid gap-1.5"
                  style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))` }}
                >
                  {stats.map(([k, v]) => (
                    <StatTile key={k} label={k} value={v} legendary={legendary} />
                  ))}
                </div>
              )}

              <div className="mt-3.5">
                {listing.snapshot.species ? (
                  <FactRow label={t("marketplace.detail.species")}>
                    {listing.snapshot.species}
                  </FactRow>
                ) : null}
                {listing.snapshot.evo_stage != null ? (
                  <FactRow label={t("marketplace.detail.evoStage")}>
                    {listing.snapshot.evo_stage}
                  </FactRow>
                ) : null}
                {trait ? (
                  <FactRow label={t("marketplace.detail.personality")}>
                    <span className="capitalize">
                      {traitLabel(trait, i18n.language?.startsWith("en") ? "en" : "de")}
                    </span>
                  </FactRow>
                ) : null}
              </div>
            </div>
          </div>
        </Panel>

        <Panel tone="secondary" contentClassName="space-y-3.5 p-3.5">
          {listing.status !== "active" ? (
            <div className="rounded-md border-2 border-ink bg-card px-3 py-2.5 text-sm text-muted-foreground">
              {listing.status === "sold"
                ? t("marketplace.detail.soldNote")
                : listing.status === "expired"
                  ? t("marketplace.detail.expiredNote")
                  : t("marketplace.detail.cancelled")}
            </div>
          ) : isOwn ? (
            <>
              <div className="text-[13px] text-muted-foreground">
                {t("marketplace.detail.ownListingNote")}
              </div>
              {listing.current_bidder_id == null && (
                <Button
                  variant="destructive"
                  onClick={() => cancelM.mutate()}
                  disabled={cancelM.isPending}
                  className="w-full"
                >
                  {cancelM.isPending && <Loader2 className="animate-spin" strokeWidth={2.5} />}
                  {t("marketplace.detail.cancel")}
                </Button>
              )}
            </>
          ) : isAuction ? (
            <>
              <div>
                {/* Ohne Gebot steht dort der Startpreis — ihn „Gebot“ zu nennen
                    wäre gelogen. */}
                <MicroLabel>
                  {hasBid ? t("marketplace.card.currentBid") : t("marketplace.card.startingBid")}
                </MicroLabel>
                {currentPrice != null ? (
                  <GoldAmount value={currentPrice} size="lg" className="mt-0.5" />
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
                {!hasBid ? (
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {t("marketplace.card.noBid")}
                  </div>
                ) : null}
              </div>

              <div className="flex items-center justify-between gap-2 text-[12px]">
                <MicroLabel className="flex items-center gap-1">
                  <Hourglass className="h-3 w-3" strokeWidth={2.5} />
                  {t("marketplace.card.endsIn")}
                </MicroLabel>
                <span className={cn("font-extrabold tabular-nums", urgent && "text-primary")}>
                  {formatRemaining(listing.ends_at, t)}
                </span>
              </div>

              {ended ? (
                <div className="rounded-md border-2 border-ink bg-card px-3 py-2.5 text-sm text-muted-foreground">
                  {t("marketplace.card.ended")}
                </div>
              ) : (
                <div className="space-y-1.5">
                  <label
                    htmlFor="bid-amount"
                    className="flex flex-wrap items-baseline justify-between gap-2"
                  >
                    <MicroLabel>{t("marketplace.detail.yourBid")}</MicroLabel>
                    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                      {t("marketplace.detail.minBid")}
                      <GoldAmount value={minBid} />
                    </span>
                  </label>
                  <Input
                    id="bid-amount"
                    type="number"
                    min={minBid}
                    inputMode="numeric"
                    className="tabular-nums"
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                  />
                  <Button
                    onClick={() => bidM.mutate(Number(bidAmount))}
                    disabled={bidM.isPending || Number(bidAmount) < minBid}
                    className="w-full"
                  >
                    {bidM.isPending && <Loader2 className="animate-spin" strokeWidth={2.5} />}
                    <Gavel strokeWidth={2.5} />
                    {t("marketplace.detail.placeBid")}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <MicroLabel>{t("marketplace.card.buyNow")}</MicroLabel>
                {listing.asking_price != null ? (
                  <GoldAmount value={listing.asking_price} size="lg" className="mt-0.5" />
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
              <Button
                onClick={() => {
                  if (
                    window.confirm(
                      t("marketplace.detail.buyConfirm").replace(
                        "{{price}}",
                        String(listing.asking_price),
                      ),
                    )
                  ) {
                    buyM.mutate();
                  }
                }}
                disabled={buyM.isPending}
                className="w-full"
              >
                {buyM.isPending && <Loader2 className="animate-spin" strokeWidth={2.5} />}
                <Store strokeWidth={2.5} />
                {t("marketplace.card.buyNow")}
              </Button>
            </>
          )}

          {isAuction && (
            <div className="border-t-2 border-ink pt-3">
              <MicroLabel className="mb-1.5">{t("marketplace.detail.bidHistory")}</MicroLabel>
              {bids.length === 0 ? (
                <p className="text-[12px] text-muted-foreground">
                  {t("marketplace.detail.noBids")}
                </p>
              ) : (
                <ul className="max-h-60 overflow-y-auto">
                  {bids.map((b) => (
                    <li
                      key={b.id}
                      className="flex items-center justify-between gap-2 border-t-2 border-ink/40 py-1.5 first:border-t-0"
                    >
                      <GoldAmount value={b.amount} />
                      <span className="text-[11px] tabular-nums text-muted-foreground">
                        {new Date(b.created_at).toLocaleTimeString()}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Panel>
      </div>
    </ScreenContainer>
  );
}
